// frontend/src/components/schedule/EventDetailsPanel.js
import React, { useState } from 'react';
import { 
  X, Edit2, Trash2, Clock, User, Plane, Calendar, 
  CheckCircle, XCircle, DollarSign, FileText, AlertCircle, Fuel,
  History
} from 'lucide-react';
import EventAuditLog from './EventAuditLog';
import EventActionModal from './EventActionModal';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_STEPS = [
  { key: 'check_in', label: 'Check-in', icon: CheckCircle, color: 'green' },
  { key: 'check_out', label: 'Check-out', icon: XCircle, color: 'red' },
  { key: 'billing', label: 'Billing', icon: DollarSign, color: 'yellow' },
  { key: 'grading', label: 'Grading', icon: FileText, color: 'blue' },
  { key: 'squawk', label: 'Squawk', icon: AlertCircle, color: 'orange' },
  { key: 'fuel', label: 'Fuel', icon: Fuel, color: 'cyan' }
];

const EVENT_TYPES = {
  flight: { label: 'Flight', color: 'blue' },
  fuel: { label: 'Fuel', color: 'orange' },
  maintenance: { label: 'Maintenance', color: 'red' },
  meeting: { label: 'Meeting', color: 'purple' },
  appointment: { label: 'Appointment', color: 'green' }
};

const EventDetailsPanel = ({ 
  event, 
  isOpen, 
  onClose, 
  onEdit, 
  onDelete, 
  onUpdateStatus,
  canManageEvents,
  updatingStatus 
}) => {
  const { hasMinLevel } = useAuth();
  const isManager = hasMinLevel(75);
  const [showHistory, setShowHistory] = useState(false);
  const [actionModal, setActionModal] = useState({ isOpen: false, action: null });

  if (!isOpen || !event) return null;

  const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.appointment;
  const progress = event.progress || 0;

  const handleActionClick = (actionKey) => {
    setActionModal({ isOpen: true, action: actionKey });
  };

  const handleActionSuccess = () => {
    if (onUpdateStatus) {
      onUpdateStatus(event.id, actionModal.action, true);
    }
    setActionModal({ isOpen: false, action: null });
  };

  const isCompleted = (key) => {
    switch(key) {
      case 'check_in': return event.check_in;
      case 'check_out': return event.check_out;
      case 'billing': return event.billing_status === 'paid';
      case 'grading': return !!event.grading;
      case 'squawk': return !!event.squawk;
      case 'fuel': return event.fuel_reimbursed;
      default: return false;
    }
  };

  return (
    <>
      <div className="w-2/5 ml-4 bg-gray-800/50 rounded-xl border border-gray-700 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800/95 p-4 border-b border-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg bg-${eventType.color}-500/20`}>
              <Calendar className={`w-5 h-5 text-${eventType.color}-400`} />
            </div>
            <div>
              <h3 className="text-white font-semibold">{event.title}</h3>
              <p className="text-xs text-gray-400">{eventType.label}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`p-1.5 rounded-lg transition ${showHistory ? 'bg-blue-500/20 text-blue-400' : 'hover:bg-gray-700 text-gray-400'}`}
              title="Historique"
            >
              <History className="w-4 h-4" />
            </button>
            {canManageEvents && (
              <>
                <button
                  onClick={() => onEdit(event)}
                  className="p-1.5 hover:bg-gray-700 rounded-lg text-blue-400"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(event.id)}
                  className="p-1.5 hover:bg-gray-700 rounded-lg text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Date et heure */}
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Clock className="w-4 h-4" />
            <span>
              {new Date(event.start_time).toLocaleString()} - {new Date(event.end_time).toLocaleTimeString()}
            </span>
          </div>

          {/* Description */}
          {event.description && (
            <div className="border-t border-gray-700 pt-3">
              <p className="text-gray-400 text-sm whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          {/* Informations associées */}
          <div className="border-t border-gray-700 pt-3 space-y-2">
            {event.staff_first_name && (
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-white">{event.staff_first_name} {event.staff_last_name}</span>
              </div>
            )}
            {event.tail_number && (
              <div className="flex items-center gap-2 text-sm">
                <Plane className="w-4 h-4 text-gray-400" />
                <span className="text-white">{event.tail_number}</span>
              </div>
            )}
          </div>

          {/* Section Historique (si activée) */}
          {showHistory && (
            <div className="border-t border-gray-700 pt-3">
              <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-2">
                <History className="w-4 h-4 text-gray-400" />
                Action History
              </h4>
              <EventAuditLog eventId={event.id} onRefresh={() => onUpdateStatus && onUpdateStatus(event.id, null, null)} />
            </div>
          )}

          {/* Section Progression (si historique fermé) */}
          {!showHistory && (
            <div className="border-t border-gray-700 pt-3">
              <h4 className="text-white text-sm font-medium mb-3">Progression</h4>
              <div className="space-y-3">
                {STATUS_STEPS.map((step) => {
                  const StepIcon = step.icon;
                  const completed = isCompleted(step.key);
                  
                  return (
                    <div key={step.key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StepIcon className={`w-4 h-4 ${completed ? `text-${step.color}-400` : 'text-gray-500'}`} />
                        <span className={`text-sm ${completed ? `text-${step.color}-400` : 'text-gray-400'}`}>
                          {step.label}
                        </span>
                      </div>
                      {canManageEvents && !completed && (
                        <button
                          onClick={() => handleActionClick(step.key)}
                          disabled={updatingStatus}
                          className="px-2 py-1 rounded text-xs transition bg-gray-700 text-gray-400 hover:bg-gray-600"
                        >
                          Marquer
                        </button>
                      )}
                      {completed && (
                        <span className="text-xs text-green-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Done
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Barre de progression globale */}
          <div className="border-t border-gray-700 pt-3">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Overall progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'action */}
      <EventActionModal
        isOpen={actionModal.isOpen}
        onClose={() => setActionModal({ isOpen: false, action: null })}
        eventId={event.id}
        eventTitle={event.title}
        action={actionModal.action}
        onSuccess={handleActionSuccess}
      />
    </>
  );
};

export default EventDetailsPanel;